import React from "react";
import { Box, Button, Collapse, Paper, Stack } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";

/**
 * Liste sayfaları (dosyalar, ekip, hastalar, formüller) için ortak filtre kabuğu: aç/kapa, panel, Apply/Clear.
 * Alanları `children` ile verin; state her sayfada kalır.
 */
export default function CollapsibleListFilters({
  colors,
  open,
  onToggle,
  activeFilterCount = 0,
  onApply,
  onClear,
  children,
  exportAction = null,
  /** Apply/Clear ile aynı satırda ek kontroller (ör. Sort By / Direction) */
  trailingControls = null,
  filtersLabel = "Filters",
  activeLabel = "active",
  applyLabel = "Apply Filters",
  clearLabel = "Clear",
}) {
  const activeSuffix =
    activeFilterCount > 0 ? ` (${activeFilterCount} ${activeLabel})` : "";

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{
          mb: 2,
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "flex-start" },
        }}
      >
        <Button
          variant="contained"
          startIcon={<FilterListIcon />}
          onClick={onToggle}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            alignSelf: "flex-start",
            background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
            color: colors.grey[100],
            px: 2.5,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${colors.blueAccent[400]}50`,
            boxShadow: `0 4px 14px ${colors.primary[900]}50`,
            "&:hover": {
              background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
              boxShadow: `0 6px 18px ${colors.greenAccent[800]}40`,
            },
          }}
        >
          {filtersLabel}
          {activeSuffix}
        </Button>
        {exportAction ? (
          <Box sx={{ alignSelf: { xs: "stretch", md: "flex-start" } }}>{exportAction}</Box>
        ) : null}
      </Stack>

      <Collapse in={open} timeout="auto">
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            borderRadius: "16px",
            background: `linear-gradient(155deg, ${colors.primary[500]}e6 0%, ${colors.primary[600]}cc 55%, ${colors.primary[700]}b3 100%)`,
            border: `1px solid ${colors.blueAccent[500]}35`,
            boxShadow: `0 8px 28px ${colors.primary[900]}55`,
            "& .MuiOutlinedInput-root": {
              backgroundColor: `${colors.primary[700]}70`,
              borderRadius: 2,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `${colors.blueAccent[500]}40`,
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: `${colors.greenAccent[500]}45`,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${colors.greenAccent[400]}`,
              borderWidth: 1,
            },
            "& .MuiInputLabel-root": {
              color: colors.grey[400],
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
            "& .MuiSelect-icon": {
              color: colors.grey[400],
            },
            /* Tarih alanı takvim simgesi (Chromium / Safari): koyu ikon → tema ile uyumlu açık ton */
            "& .MuiInputBase-input[type='date'], & .MuiInputBase-input[type='datetime-local']": {
              colorScheme: "dark",
            },
            "& .MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator, & .MuiInputBase-input[type='datetime-local']::-webkit-calendar-picker-indicator":
              {
                cursor: "pointer",
                opacity: 0.95,
                filter: `invert(1) brightness(1.15)`,
              },
            "& .MuiInputBase-input[type='date']:hover::-webkit-calendar-picker-indicator, & .MuiInputBase-input[type='datetime-local']:hover::-webkit-calendar-picker-indicator":
              {
                filter: `invert(1) brightness(1.45)`,
              },
          }}
        >
          <Box>{children}</Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 2.5 }}
            flexWrap="wrap"
          >
            <Button
              variant="contained"
              onClick={onApply}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                "&:hover": { backgroundColor: colors.greenAccent[500] },
              }}
            >
              {applyLabel}
            </Button>
            <Button
              variant="outlined"
              onClick={onClear}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderColor: `${colors.blueAccent[400]}80`,
                color: colors.grey[100],
                "&:hover": {
                  borderColor: colors.greenAccent[400],
                  backgroundColor: `${colors.blueAccent[800]}30`,
                },
              }}
            >
              {clearLabel}
            </Button>
            {trailingControls}
          </Stack>
        </Paper>
      </Collapse>
    </>
  );
}

/**
 * Filtre panelindeki Select (ör. Gender, Role) açılır menüsü — ana panel ile uyumlu koyu gradient ve öğe stilleri.
 * TextField `select` + `SelectProps={listFiltersSelectProps(colors)}` ile kullanın.
 */
export function listFiltersSelectProps(colors) {
  return {
    MenuProps: {
      disableAutoFocusItem: true,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      PaperProps: {
        elevation: 0,
        sx: {
          mt: 0.75,
          borderRadius: "12px",
          minWidth: 200,
          maxHeight: 360,
          overflow: "auto",
          background: `linear-gradient(165deg, ${colors.primary[600]} 0%, ${colors.primary[800]}ee 100%)`,
          border: `1px solid ${colors.blueAccent[500]}55`,
          boxShadow: `0 18px 44px rgba(15, 23, 42, 0.55), inset 0 1px 0 ${colors.greenAccent[500]}18`,
          "& .MuiList-root": {
            py: 1,
            px: 0.5,
          },
          "& .MuiMenuItem-root": {
            borderRadius: "8px",
            mx: 0.5,
            my: 0.25,
            minHeight: 40,
            fontSize: "0.875rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: colors.grey[100],
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
              backgroundColor: `${colors.blueAccent[600]}55`,
            },
            "&.Mui-selected": {
              backgroundColor: `${colors.greenAccent[600]}40`,
              color: colors.grey[100],
              "&:hover": {
                backgroundColor: `${colors.greenAccent[500]}50`,
              },
            },
            "&.Mui-focusVisible": {
              backgroundColor: `${colors.blueAccent[700]}44`,
            },
          },
        },
      },
    },
  };
}

/** Üst araç çubuğundaki Excel indirme butonu — liste sayfalarıyla aynı gradient dilimi */
export function ListFiltersExcelButton({
  colors,
  onClick,
  disabled = false,
  loading = false,
  label = "Export Excel",
  loadingLabel = "Exporting...",
}) {
  return (
    <Button
      variant="contained"
      startIcon={<DownloadIcon />}
      onClick={onClick}
      disabled={disabled}
      sx={{
        textTransform: "none",
        fontWeight: 700,
        alignSelf: "flex-start",
        background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
        color: colors.grey[100],
        px: 2.5,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${colors.greenAccent[400]}45`,
        boxShadow: `0 4px 16px ${colors.greenAccent[900]}40`,
        "&:hover": {
          background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
          boxShadow: `0 6px 20px ${colors.blueAccent[600]}45`,
        },
        "&:disabled": {
          background: colors.grey[700],
          color: colors.grey[400],
          borderColor: colors.grey[600],
        },
      }}
    >
      {loading ? loadingLabel : label}
    </Button>
  );
}
